// Explore's results as a file: the page of products on screen, each read in
// full, with the fields the user picks from what the product page shows.

import {
  CapabilityClaim,
  ProductDetail,
  ProductLinkGroup,
  ProductStructureLink,
  Stance,
} from "../../types/productPlane";
import {
  ExportField,
  ExportFieldOption,
  columnField,
  tableOfFields,
} from "../../utils/export/fields";
import { pageNote } from "../../utils/export/pages";
import {
  ExportCell,
  ExportTable,
  TableFormat,
  writeTableExport,
} from "../../utils/export/table";
import {
  CompareGroup,
  CompareGroupId,
  buildCompareModel,
  cellText,
  rowLabel,
  singleSourceShare,
} from "../Compare/compareModel";
import { codeWords, dateRange } from "../Detail/format";
import { ActiveFilter } from "../Filters/ActiveFilterChips";
import { ROLE_ORDER, makerEntries } from "../ProductDetail/productRelations";
import { ExploreState } from "./exploreState";
import { UNNAMED_PRODUCT } from "./productFields";

// What the About sheet names as the source of the file
const SOURCE = "Market Intelligence · Explore";

// Products read at once while an export reads the page
const READ_CONCURRENCY = 6;

// A date as the product holds it: a day as a date; a month or a year, which
// a date would give a day it does not have, as the text
const dayOf = (day: string | null | undefined): Date | string | null => {
  if (!day) {
    return null;
  }
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? new Date(`${day}T00:00:00Z`) : day;
};

// The address of a product's page
const productUrl = (id: string): string =>
  `${window.location.origin}/entity/${encodeURIComponent(id)}`;

const named = (ref: { id: string; name: string | null }): string =>
  ref.name ?? ref.id;

// A name with what is said of it in brackets: "Acme (US, developer)"
const noted = (name: string, notes: (string | null)[]): string => {
  const said = notes.filter(Boolean);
  return said.length > 0 ? `${name} (${said.join(", ")})` : name;
};

// The organizations in one role, by name, with their country
const organizationsIn =
  (role: string) =>
  (product: ProductDetail): string[] =>
    product.organizations
      .filter((group) => group.role === role)
      .flatMap((group) => group.items)
      .map((item) => noted(named(item.organization), [item.country]));

// The makers as the head names them: the manufacturers, then the
// developers, each once; one that only develops it, or that is only
// inferred, says so
const makerNames = (product: ProductDetail): string[] =>
  makerEntries(product.organizations).map((entry) =>
    noted(named(entry.organization), [
      entry.country,
      entry.roles.includes("maker") ? null : "developer",
      entry.likely ? "likely" : null,
    ]),
  );

// The capabilities claimed with one stance, by name, each once
const capabilitiesWith =
  (stance: Stance) =>
  (product: ProductDetail): string[] =>
    Array.from(
      new Set(
        product.claims
          .filter((claim: CapabilityClaim) => claim.stance === stance)
          .map((claim) => named(claim.capability.capability)),
      ),
    );

// The entities linked by any relation of the groups: "Polaris (procured
// under)"
const linkedIn =
  (groups: (product: ProductDetail) => ProductLinkGroup[]) =>
  (product: ProductDetail): string[] =>
    groups(product).flatMap((group) =>
      group.items.map(
        (item) => `${named(item.entity)} (${codeWords(group.relation)})`,
      ),
    );

const productsIn =
  (links: (product: ProductDetail) => ProductStructureLink[]) =>
  (product: ProductDetail): string[] =>
    links(product).map((link) => named(link.product));

// The comparison of the products exported, built once per export: the
// fields that spread take their columns and their cells from it
const models = new WeakMap<readonly ProductDetail[], CompareGroup[]>();

const modelOf = (rows: readonly ProductDetail[]): CompareGroup[] => {
  let model = models.get(rows);
  if (!model) {
    model = buildCompareModel(
      rows.map((product) => ({ id: product.id, product })),
      null,
    );
    models.set(rows, model);
  }
  return model;
};

// A field that writes a column per row of a group of the comparison: one
// per specification, per capability, per standard
const spreadField = (
  option: ExportFieldOption,
  groupId: CompareGroupId,
  width: number,
): ExportField<ProductDetail> => ({
  ...option,
  columns: (rows) => {
    const group = modelOf(rows).find((entry) => entry.id === groupId);
    if (!group) {
      return [];
    }
    const index = new Map(rows.map((product, at) => [product, at]));
    return group.rows.map((row) => ({
      header: rowLabel(row),
      width,
      value: (product: ProductDetail): ExportCell => {
        const at = index.get(product);
        return at === undefined ? null : cellText(row.cells[at]);
      },
    }));
  },
});

const field = (
  key: string,
  label: string,
  group: string,
  selected: boolean,
  value: (product: ProductDetail) => ExportCell,
  column: { kind?: "number" | "date" | "url"; width?: number } = {},
): ExportField<ProductDetail> =>
  columnField({ key, label, group, selected }, { value, ...column });

const PRODUCT = "Product";
const CLASS = "Classification";
const ORGANIZATIONS = "Organizations";
const CLAIMS = "Capabilities";
const SPECIFICATIONS = "Specifications and standards";
const CONTEXT = "Context and structure";
const RECORD = "Record";

// The roles past the makers, with the heading of their column
const OTHER_ROLES = ROLE_ORDER.filter(
  (role) => role !== "maker" && role !== "developer",
);
const ROLE_LABELS: Record<string, string> = {
  operator: "Operators",
  buyer: "Buyers",
  funder: "Funders",
  owner: "Owners",
  other: "Other organizations",
};

// What the user may export of a product: what its page shows, in the
// page's order. The operators are ticked; the rarer roles are not.
export const PRODUCT_EXPORT_FIELDS: ExportField<ProductDetail>[] = [
  field("name", "Name", PRODUCT, true, (p) => p.name ?? UNNAMED_PRODUCT, {
    width: 36,
  }),
  field("other_names", "Other names", PRODUCT, true, (p) => p.other_names, {
    width: 32,
  }),
  field("original_name", "Original name", PRODUCT, false, (p) => p.original_name),
  field("description", "Description", PRODUCT, true, (p) => p.description, {
    width: 60,
  }),
  field("page", "Product page", PRODUCT, true, (p) => productUrl(p.id), {
    kind: "url",
    width: 40,
  }),
  field("id", "ID", PRODUCT, false, (p) => p.id, { width: 30 }),

  field(
    "category",
    "Category",
    CLASS,
    true,
    (p) => p.categories.map((entry) => named(entry.category)),
    { width: 28 },
  ),
  field(
    "category_path",
    "Category path",
    CLASS,
    false,
    (p) => p.categories.map((entry) => entry.path.map(named).join(" › ")),
    { width: 48 },
  ),
  field("category_stated", "Category as stated", CLASS, false, (p) => p.category_stated, {
    width: 28,
  }),
  field("level", "Level", CLASS, true, (p) => codeWords(p.level) || null),
  field("status", "Status", CLASS, true, (p) =>
    p.status ? codeWords(p.status.status) : null,
  ),
  field("status_since", "Status since", CLASS, false, (p) => dayOf(p.status?.since)),
  field("status_until", "Status until", CLASS, false, (p) => dayOf(p.status?.until)),
  field("status_wording", "Status as stated", CLASS, false, (p) => p.status?.wording ?? null, {
    width: 40,
  }),
  field("integration", "Integration", CLASS, false, (p) =>
    p.integration && p.integration !== "unclear" ? codeWords(p.integration) : null,
  ),
  field(
    "typical_hosts",
    "Typical hosts",
    CLASS,
    true,
    (p) => p.typical_hosts.map((host) => named(host.category)),
    { width: 32 },
  ),

  field("makers", "Makers", ORGANIZATIONS, true, makerNames, { width: 40 }),
  ...OTHER_ROLES.map((role) =>
    field(role, ROLE_LABELS[role] ?? codeWords(role), ORGANIZATIONS, role === "operator", organizationsIn(role), {
      width: 40,
    }),
  ),

  field("capabilities_has", "Has", CLAIMS, true, capabilitiesWith("has"), {
    width: 48,
  }),
  field("capabilities_enables", "Enables", CLAIMS, true, capabilitiesWith("enables"), {
    width: 40,
  }),
  field("capabilities_requires", "Requires", CLAIMS, false, capabilitiesWith("requires"), {
    width: 40,
  }),
  field("capabilities_lacks", "Lacks", CLAIMS, false, capabilitiesWith("lacks"), {
    width: 40,
  }),
  spreadField(
    {
      key: "capability_columns",
      label: "Capability details",
      group: CLAIMS,
      selected: false,
      hint: "a column per capability, with its conditions",
    },
    "capabilities",
    32,
  ),

  spreadField(
    {
      key: "specifications",
      label: "Specifications",
      group: SPECIFICATIONS,
      selected: true,
      hint: "a column per specification",
    },
    "specifications",
    20,
  ),
  field(
    "standards",
    "Standards",
    SPECIFICATIONS,
    true,
    (p) =>
      p.standards.map((entry) =>
        entry.status ? `${entry.standard} (${codeWords(entry.status)})` : entry.standard,
      ),
    { width: 40 },
  ),

  field("projects", "Programmes", CONTEXT, true, linkedIn((p) => p.projects), {
    width: 40,
  }),
  field("incidents", "Incidents", CONTEXT, false, linkedIn((p) => p.incidents), {
    width: 40,
  }),
  field("facilities", "Facilities", CONTEXT, false, linkedIn((p) => p.facilities), {
    width: 40,
  }),
  field("components", "Components", CONTEXT, false, productsIn((p) => p.structure.contains), {
    width: 40,
  }),
  field("built_into", "Built into", CONTEXT, false, productsIn((p) => p.structure.built_into), {
    width: 40,
  }),
  field("variant_of", "Variant of", CONTEXT, false, productsIn((p) => p.structure.variant_of), {
    width: 32,
  }),
  field("variants", "Variants", CONTEXT, false, productsIn((p) => p.structure.variants), {
    width: 40,
  }),

  field("sources", "Articles", RECORD, true, (p) => p.source_count, {
    kind: "number",
  }),
  field(
    "first_source",
    "First article",
    RECORD,
    false,
    (p) => dayOf(dateRange(p.sources.items.map((source) => source.date))?.first),
    { kind: "date" },
  ),
  field(
    "latest_source",
    "Latest article",
    RECORD,
    true,
    (p) => dayOf(dateRange(p.sources.items.map((source) => source.date))?.latest),
    { kind: "date" },
  ),
  field(
    "single_source",
    "Facts with one source (%)",
    RECORD,
    false,
    (p) => singleSourceShare(p),
    { kind: "number" },
  ),
];

// The products as a table of the fields picked
export const tableOfProducts = (
  keys: readonly string[],
  products: readonly ProductDetail[],
): ExportTable => tableOfFields("Products", PRODUCT_EXPORT_FIELDS, keys, products);

const SORT_WORDS: Record<ExploreState["sort"], string> = {
  relevance: "Relevance",
  sources: "Most sources",
  latest: "Latest source",
};

// The selection as label and value pairs: the keyword, each filter's picks
// and ranges as the chips say them, and the sort
export const productExportFilters = (
  state: Pick<ExploreState, "keyword" | "sort" | "unnamed">,
  active: readonly ActiveFilter[],
): [string, string][] => {
  const pairs: [string, string][] = [];
  if (state.keyword) {
    pairs.push(["Keyword", state.keyword]);
  }
  const sections = new Map<string, string[]>();
  for (const filter of active) {
    sections.set(filter.section, [
      ...(sections.get(filter.section) ?? []),
      filter.value,
    ]);
  }
  for (const [section, values] of Array.from(sections.entries())) {
    pairs.push([section, values.join(", ")]);
  }
  if (state.unnamed) {
    pairs.push(["Without a name", "included"]);
  }
  pairs.push(["Sort", SORT_WORDS[state.sort]]);
  return pairs;
};

// Reads every product, a few at a time, in the order of the ids; reports
// each one read
const readAll = async (
  ids: readonly string[],
  read: (id: string) => Promise<ProductDetail>,
  progress: (done: number, total: number) => void,
): Promise<ProductDetail[]> => {
  const products: ProductDetail[] = new Array(ids.length);
  let next = 0;
  let done = 0;
  progress(0, ids.length);
  const worker = async () => {
    while (next < ids.length) {
      const at = next++;
      products[at] = await read(ids[at]);
      progress(++done, ids.length);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(READ_CONCURRENCY, ids.length) }, worker),
  );
  return products;
};

export interface ProductExportRequest {
  state: ExploreState;
  active: readonly ActiveFilter[];
  // The ids of the products on screen, in their order
  ids: readonly string[];
  // Where the page starts among the `total` the selection matches
  offset: number;
  total: number;
  format: TableFormat;
  keys: readonly string[];
  // Reads one product in full: the read the product page makes
  read: (id: string) => Promise<ProductDetail>;
  progress: (done: number, total: number) => void;
}

// Writes the page of products on screen as a file of the fields picked
export const exportProducts = ({
  state,
  active,
  ids,
  offset,
  total,
  format,
  keys,
  read,
  progress,
}: ProductExportRequest): Promise<void> =>
  writeTableExport(format, async () => ({
    tables: [tableOfProducts(keys, await readAll(ids, read, progress))],
    meta: {
      title: "Products",
      generatedAt: new Date(),
      source: SOURCE,
      filters: productExportFilters(state, active),
      note: pageNote(offset, ids.length, total),
    },
    filename: ["explore", "products", state.keyword],
  }));
